/**
 * Typed client for spark-creatives' eight `site-seo` routes (`/creatives/casino-instance/{id}/site-seo…`), on the
 * tenant-source result taxonomy plus a `credits` branch (402 on a Lumio generation). Parsers are pure and tolerant:
 * a missing field gets its documented default, a malformed row is dropped, and only a state the page cannot work
 * without (no default language) throws, which the result maps to `failed`.
 */
import axios from 'axios';

import { creativesRequestConfig, creativesServiceBase, creativesServiceConfigured } from '../creatives/creative-api';
import { tenantSourceResult, type TenantSourceFailure } from '../creatives/creatives-tenant-source';

import { isWildcardPath, matchOfPath } from './route-path';
import { normaliseKeywordsMap } from './seo-document';
import type {
  ImageRef,
  Localized,
  SiteSeoAi,
  SiteSeoDocument,
  SiteSeoDraftSaved,
  SiteSeoFooterRule,
  SiteSeoGenerated,
  SiteSeoGenerateRequest,
  SiteSeoIssue,
  SiteSeoJob,
  SiteSeoKeywords,
  SiteSeoLiveMeta,
  SiteSeoPage,
  SiteSeoRoute,
  SiteSeoSite,
  SiteSeoState,
  SiteSeoSummary,
  SiteSeoUrl,
  SiteSeoUrls,
} from './seo-types';

/** A refused answer may carry the server's issues; `credits` is a 402 with the amount the wallet lacks. */
export type SiteSeoFailure =
  | (TenantSourceFailure & { issues?: SiteSeoIssue[] })
  | { ok: false; reason: 'credits'; needed?: number };

export type SiteSeoResult<T> = { ok: true; value: T } | SiteSeoFailure;

type Body = Record<string, unknown>;

const isRecord = (value: unknown): value is Body =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const str = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);
const finite = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/**
 * Map a `site-seo` answer onto a {@link SiteSeoResult}. Beyond {@link tenantSourceResult}: 402 is `credits`; 409 with
 * `{ error }` is `refused` (a publish or revert runs, or generation is not priced); 400 with `{ error, issues[] }` is
 * the refusal with the parsed issues attached; a parse that throws is `failed`.
 */
export const siteSeoResult = <T>(status: number, data: unknown, parse: (body: Body) => T): SiteSeoResult<T> => {
  const body = isRecord(data) ? data : {};
  if (status === 402) return { ok: false, reason: 'credits', needed: finite(body.needed) };
  const message = typeof body.error === 'string' && body.error !== '' ? body.error : undefined;
  if (status === 409 && message) return { ok: false, reason: 'refused', message };
  if (status === 400 && message && Array.isArray(body.issues)) {
    return { ok: false, reason: 'refused', message, issues: parseSiteSeoIssues(body.issues) };
  }
  try {
    return tenantSourceResult(status, data, parse);
  } catch {
    return { ok: false, reason: 'failed' };
  }
};

const parseLocalized = (value: unknown): Localized<string> | undefined => {
  if (!isRecord(value)) return undefined;
  const out: Localized<string> = {};
  for (const [language, text] of Object.entries(value)) if (typeof text === 'string') out[language] = text;
  return out;
};

/** Per language, the string items of a list, normalised as the server stores them; languages left with none dropped;
 *  undefined when nothing remains. */
const parseKeywords = (value: unknown): SiteSeoKeywords | undefined => {
  if (!isRecord(value)) return undefined;
  const lists: SiteSeoKeywords = {};
  for (const [language, list] of Object.entries(value)) if (Array.isArray(list)) lists[language] = stringList(list);
  return normaliseKeywordsMap(lists);
};

const parseImage = (value: unknown): ImageRef | undefined => {
  if (!isRecord(value) || typeof value.url !== 'string') return undefined;
  const assetId = str(value.asset_id);
  return {
    url: value.url,
    width: finite(value.width) ?? 0,
    height: finite(value.height) ?? 0,
    ...(assetId === undefined ? {} : { asset_id: assetId }),
  };
};

const parseAi = (value: unknown): SiteSeoAi => {
  const ai = isRecord(value) ? value : {};
  const summary = str(ai.llms_summary);
  const facts = str(ai.llms_facts);
  return {
    allow_search_crawlers: ai.allow_search_crawlers !== false,
    allow_training_crawlers: ai.allow_training_crawlers !== false,
    ...(summary === undefined ? {} : { llms_summary: summary }),
    ...(facts === undefined ? {} : { llms_facts: facts }),
  };
};

const parseSite = (value: unknown): SiteSeoSite => {
  const site = isRecord(value) ? value : {};
  const titleTemplate = parseLocalized(site.title_template);
  const description = parseLocalized(site.description);
  const shareImage = parseImage(site.share_image);
  const footerText = parseLocalized(site.footer_text);
  const keywords = parseKeywords(site.keywords);
  return {
    ...(titleTemplate ? { title_template: titleTemplate } : {}),
    ...(description ? { description } : {}),
    ...(shareImage ? { share_image: shareImage } : {}),
    ...(footerText ? { footer_text: footerText } : {}),
    ...(keywords ? { keywords } : {}),
    ai: parseAi(site.ai),
  };
};

/** A route, or null when its path does not start with `/` or its match is unknown. A missing match is the path's
 *  own and a missing origin is `custom`, as the server's `seo.Parse` reads them. */
const parseRoute = (value: unknown): SiteSeoRoute | null => {
  if (!isRecord(value) || typeof value.path !== 'string' || !value.path.startsWith('/')) return null;
  const { path } = value;
  const match = value.match === undefined ? matchOfPath(path) : value.match;
  if (match !== 'exact' && match !== 'pattern' && match !== 'wildcard') return null;
  const title = parseLocalized(value.title);
  const description = parseLocalized(value.description);
  const shareImage = parseImage(value.share_image);
  const footerText = parseLocalized(value.footer_text);
  const canonical = str(value.canonical);
  const keywords = parseKeywords(value.keywords);
  return {
    path,
    match,
    origin: value.origin === 'site_page' ? 'site_page' : 'custom',
    ...(title ? { title } : {}),
    ...(description ? { description } : {}),
    ...(shareImage ? { share_image: shareImage } : {}),
    ...(footerText ? { footer_text: footerText } : {}),
    ...(typeof value.noindex === 'boolean' ? { noindex: value.noindex } : {}),
    ...(canonical === undefined ? {} : { canonical }),
    ...(keywords ? { keywords } : {}),
  };
};

/** A document, or null when `value` is not an object. Drops routes without a `/` path or with an unknown match;
 *  the ai switches default to allowed. */
export const parseSiteSeoDocument = (value: unknown): SiteSeoDocument | null => {
  if (!isRecord(value)) return null;
  const routes = Array.isArray(value.routes) ? value.routes : [];
  return {
    site: parseSite(value.site),
    routes: routes.map(parseRoute).filter((r): r is SiteSeoRoute => r !== null),
  };
};

/** Every `set_pages` row with a `/` path and an exact or pattern match, whatever other keys it carries. */
export const parseSiteSeoPages = (value: unknown): SiteSeoPage[] => {
  if (!Array.isArray(value)) return [];
  const pages: SiteSeoPage[] = [];
  for (const row of value) {
    if (!isRecord(row) || typeof row.path !== 'string' || !row.path.startsWith('/')) continue;
    if (row.match !== 'exact' && row.match !== 'pattern') continue;
    const label = parseLocalized(row.label);
    const kind = str(row.kind);
    const sourceId = str(row.source_id);
    pages.push({
      path: row.path,
      match: row.match,
      ...(label ? { label } : {}),
      expanded: row.expanded === true,
      ...(kind === undefined ? {} : { kind }),
      ...(sourceId === undefined ? {} : { source_id: sourceId }),
    });
  }
  return pages;
};

const isJobStatus = (value: unknown): value is SiteSeoJob['status'] =>
  value === 'queued' || value === 'running' || value === 'done' || value === 'failed';

/** A job, or null without a string id and a known status. `action` defaults to `publish`; `confirmed` is kept
 *  only when it is a boolean. */
export const parseSiteSeoJob = (value: unknown): SiteSeoJob | null => {
  if (!isRecord(value) || typeof value.id !== 'string' || value.id === '' || !isJobStatus(value.status)) return null;
  const error = str(value.error);
  const phase = str(value.phase);
  return {
    id: value.id,
    action: value.action === 'revert' ? 'revert' : 'publish',
    status: value.status,
    ...(error === undefined ? {} : { error }),
    ...(phase === undefined ? {} : { phase }),
    ...(typeof value.confirmed === 'boolean' ? { confirmed: value.confirmed } : {}),
  };
};

/** The rows with a level of `error` or `warning` and a string code; `message` defaults to ''. */
export const parseSiteSeoIssues = (value: unknown): SiteSeoIssue[] => {
  if (!Array.isArray(value)) return [];
  const issues: SiteSeoIssue[] = [];
  for (const row of value) {
    if (!isRecord(row) || (row.level !== 'error' && row.level !== 'warning') || typeof row.code !== 'string') continue;
    const path = str(row.path);
    const field = str(row.field);
    const language = str(row.language);
    issues.push({
      level: row.level,
      code: row.code,
      ...(path === undefined ? {} : { path }),
      ...(field === undefined ? {} : { field }),
      ...(language === undefined ? {} : { language }),
      message: str(row.message) ?? '',
    });
  }
  return issues;
};

const parseFooterRules = (value: unknown): SiteSeoFooterRule[] => {
  if (!Array.isArray(value)) return [];
  const rules: SiteSeoFooterRule[] = [];
  for (const row of value) {
    if (!isRecord(row) || typeof row.path !== 'string') continue;
    rules.push({
      path: row.path,
      text: parseLocalized(row.text) ?? {},
      origin: row.origin === 'owner' ? 'owner' : 'system',
    });
  }
  return rules;
};

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

/** The full state. THROWS when `languages` is missing or its default is '' (the result maps it to `failed`). */
export const parseSiteSeoState = (body: Body): SiteSeoState => {
  const languages = isRecord(body.languages) ? body.languages : null;
  const defaultLanguage = languages ? str(languages.default) : undefined;
  if (!languages || !defaultLanguage) throw new Error('site-seo: no default language');
  const live = isRecord(body.live) ? body.live : {};
  const site = isRecord(body.site) ? body.site : {};
  return {
    draft: parseSiteSeoDocument(body.draft),
    published: parseSiteSeoDocument(body.published),
    prev_published: parseSiteSeoDocument(body.prev_published),
    live: { footer_rules: parseFooterRules(live.footer_rules) },
    can_revert: body.can_revert === true,
    languages: { default: defaultLanguage, all: stringList(languages.all) },
    pages: parseSiteSeoPages(body.pages),
    site: { brand_name: str(site.brand_name) ?? '', host: str(site.host) ?? '' },
    footer_overridable: body.footer_overridable === true,
    footer_source_id: str(body.footer_source_id) ?? '',
    footer_reference: body.footer_reference !== false,
    publishing: parseSiteSeoJob(body.publishing),
    llms_preview: str(body.llms_preview) ?? '',
    issues: parseSiteSeoIssues(body.issues),
    confirmed: body.confirmed !== false,
  };
};

/** The hub card's summary: booleans only when `true`, counts only when finite numbers (else 0). */
export const parseSiteSeoSummary = (body: Body): SiteSeoSummary => ({
  available: body.available === true,
  has_document: body.has_document === true,
  dirty: body.dirty === true,
  pages: finite(body.pages) ?? 0,
  without_description: finite(body.without_description) ?? 0,
  publishing: parseSiteSeoJob(body.publishing),
  confirmed: body.confirmed !== false,
});

const LIVE_META_KEYS = ['title', 'description', 'og_image', 'robots', 'canonical'] as const;

/** The answer's `meta` per language: string values of the known keys only; {} when `meta` is absent. */
export const parseLiveMeta = (body: Body): Localized<SiteSeoLiveMeta> => {
  const out: Localized<SiteSeoLiveMeta> = {};
  if (!isRecord(body.meta)) return out;
  for (const [language, values] of Object.entries(body.meta)) {
    if (!isRecord(values)) continue;
    const meta: SiteSeoLiveMeta = {};
    for (const key of LIVE_META_KEYS) {
      const value = values[key];
      if (typeof value === 'string') meta[key] = value;
    }
    out[language] = meta;
  }
  return out;
};

/** The `GET site-seo/urls` answer: rows with a `/` path (a missing title is ''), and the total (else the row count). */
export const parseSiteSeoUrls = (body: Body): SiteSeoUrls => {
  const urls: SiteSeoUrl[] = [];
  for (const row of Array.isArray(body.urls) ? body.urls : []) {
    if (!isRecord(row) || typeof row.path !== 'string' || !row.path.startsWith('/')) continue;
    urls.push({ path: row.path, title: str(row.title) ?? '' });
  }
  return { urls, total: finite(body.total) ?? urls.length };
};

const parseJobAnswer = (body: Body): SiteSeoJob => {
  const job = parseSiteSeoJob(body.job);
  if (!job) throw new Error('site-seo: no job in the answer');
  return job;
};

const parseDraftSaved = (body: Body): SiteSeoDraftSaved => {
  const draft = parseSiteSeoDocument(body.draft);
  if (!draft) throw new Error('site-seo: no draft in the answer');
  return { draft, issues: parseSiteSeoIssues(body.issues), llms_preview: str(body.llms_preview) ?? '' };
};

const siteSeoPath = (casinoInstanceId: string): string =>
  `${creativesServiceBase()}/creatives/casino-instance/${encodeURIComponent(casinoInstanceId)}/site-seo`;

/** One call: `unavailable` without a configured service (no request), `failed` when the request throws. */
const call = async <T>(
  request: () => Promise<{ status: number; data: unknown }>,
  parse: (body: Body) => T,
): Promise<SiteSeoResult<T>> => {
  if (!creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
  try {
    const response = await request();
    return siteSeoResult(response.status, response.data, parse);
  } catch {
    return { ok: false, reason: 'failed' };
  }
};

export const getSiteSeo = (casinoInstanceId: string, signal?: AbortSignal): Promise<SiteSeoResult<SiteSeoState>> =>
  call(() => axios.get(siteSeoPath(casinoInstanceId), creativesRequestConfig(signal)), parseSiteSeoState);

export const getSiteSeoSummary = (
  casinoInstanceId: string,
  signal?: AbortSignal,
): Promise<SiteSeoResult<SiteSeoSummary>> =>
  call(
    () => axios.get(`${siteSeoPath(casinoInstanceId)}/summary`, creativesRequestConfig(signal)),
    parseSiteSeoSummary,
  );

/** The live meta of an exact or pattern path. A wildcard carries no meta: answered `{}` without a request. */
export const getSiteSeoLiveMeta = async (
  casinoInstanceId: string,
  path: string,
  signal?: AbortSignal,
): Promise<SiteSeoResult<Localized<SiteSeoLiveMeta>>> => {
  if (isWildcardPath(path)) return { ok: true, value: {} };
  return call(
    () =>
      axios.get(
        `${siteSeoPath(casinoInstanceId)}/live?path=${encodeURIComponent(path)}`,
        creativesRequestConfig(signal),
      ),
    parseLiveMeta,
  );
};

/** Search the site's real urls (the Add route picker): `q` a substring of the path or title, `prefix` the start of
 *  the path, `limit` the rows answered (the server clamps it to 1-50). Only the given filters are sent. */
export const getSiteSeoUrls = (
  casinoInstanceId: string,
  q: { q?: string; prefix?: string; limit?: number },
  signal?: AbortSignal,
): Promise<SiteSeoResult<SiteSeoUrls>> => {
  const params = new URLSearchParams();
  if (q.q !== undefined) params.set('q', q.q);
  if (q.prefix !== undefined) params.set('prefix', q.prefix);
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  const query = params.toString();
  return call(
    () => axios.get(`${siteSeoPath(casinoInstanceId)}/urls${query ? `?${query}` : ''}`, creativesRequestConfig(signal)),
    parseSiteSeoUrls,
  );
};

export const saveSiteSeoDraft = (
  casinoInstanceId: string,
  doc: SiteSeoDocument,
  signal?: AbortSignal,
): Promise<SiteSeoResult<SiteSeoDraftSaved>> =>
  call(() => axios.put(`${siteSeoPath(casinoInstanceId)}/draft`, doc, creativesRequestConfig(signal)), parseDraftSaved);

/** Queue a publish: 202 `{ job }`. */
export const publishSiteSeo = (
  casinoInstanceId: string,
  doc: SiteSeoDocument,
  signal?: AbortSignal,
): Promise<SiteSeoResult<SiteSeoJob>> =>
  call(
    () => axios.post(`${siteSeoPath(casinoInstanceId)}/publish`, doc, creativesRequestConfig(signal)),
    parseJobAnswer,
  );

export const getSiteSeoJob = (
  casinoInstanceId: string,
  jobId: string,
  signal?: AbortSignal,
): Promise<SiteSeoResult<SiteSeoJob>> =>
  call(
    () =>
      axios.get(
        `${siteSeoPath(casinoInstanceId)}/publish/${encodeURIComponent(jobId)}`,
        creativesRequestConfig(signal),
      ),
    parseJobAnswer,
  );

/** Queue a revert: 202 `{ job }`. */
export const revertSiteSeo = (casinoInstanceId: string, signal?: AbortSignal): Promise<SiteSeoResult<SiteSeoJob>> =>
  call(() => axios.post(`${siteSeoPath(casinoInstanceId)}/revert`, {}, creativesRequestConfig(signal)), parseJobAnswer);

/** Lumio texts for the asked fields only; an answer that writes none of them is `failed` (§4.3). */
export const generateSiteSeo = async (
  casinoInstanceId: string,
  req: SiteSeoGenerateRequest,
  signal?: AbortSignal,
): Promise<SiteSeoResult<SiteSeoGenerated>> => {
  const result = await call(
    () => axios.post(`${siteSeoPath(casinoInstanceId)}/generate`, req, creativesRequestConfig(signal)),
    (body): SiteSeoGenerated => {
      const generated: SiteSeoGenerated = {};
      for (const field of req.fields) {
        const value = body[field];
        if (typeof value === 'string' && value !== '') generated[field] = value;
      }
      return generated;
    },
  );
  if (result.ok && Object.keys(result.value).length === 0) return { ok: false, reason: 'failed' };
  return result;
};
